import { getSupabaseClient } from "../../providers/supabase/supabase";

// Zonas del prototipo (base de operación: Querétaro)
export const ZONAS: Record<string, string[] | null> = {
  Querétaro: ["Querétaro"],
  "Bajío y vecinos": [
    "Guanajuato",
    "San Luis Potosí",
    "Hidalgo",
    "Michoacán",
    "Estado de México",
    "Aguascalientes",
  ],
  "Resto del país": null, // todo lo demás
};

export const ESTADOS = [
  "Aguascalientes",
  "Baja California",
  "Baja California Sur",
  "Campeche",
  "Chiapas",
  "Chihuahua",
  "Ciudad de México",
  "Coahuila",
  "Colima",
  "Durango",
  "Estado de México",
  "Guanajuato",
  "Guerrero",
  "Hidalgo",
  "Jalisco",
  "Michoacán",
  "Morelos",
  "Nayarit",
  "Nuevo León",
  "Oaxaca",
  "Puebla",
  "Querétaro",
  "Quintana Roo",
  "San Luis Potosí",
  "Sinaloa",
  "Sonora",
  "Tabasco",
  "Tamaulipas",
  "Tlaxcala",
  "Veracruz",
  "Yucatán",
  "Zacatecas",
];

export const SECTORES = [
  "Comercio Mayorista",
  "Comercio Minorista",
  "Construcción",
  "Educación",
  "Entretenimiento",
  "Hospedaje",
  "Inmobiliario",
  "Manufactura",
  "Otro",
  "Restaurantes",
  "Salud",
  "Sector Público",
  "Servicios Empresariales",
  "Servicios Financieros",
  "Servicios de Información",
  "Servicios Personales",
  "Servicios Profesionales",
  "Tecnología",
  "Transporte",
];

// Calificación en Google: [código, texto, operador, valor]
export const RATINGS: [string, string, string, number][] = [
  ["ge4.5", "4.5 ★ o más", "gte", 4.5],
  ["ge4", "4 ★ o más", "gte", 4],
  ["lt4", "Menos de 4 ★", "lt", 4],
  ["lt3.5", "Menos de 3.5 ★", "lt", 3.5],
];

const zonaEstados = (z: string) =>
  ZONAS[z] ?? ESTADOS.filter((e) => !Object.values(ZONAS).flat().includes(e));

// Valores para "in.(...)" de PostgREST, entre comillas por acentos y espacios
const inList = (vals: string[]) =>
  `(${vals.map((v) => `"${v.replace(/"/g, "")}"`).join(",")})`;

/**
 * Traduce los filtros de la pantalla (claves con "_") a filtros de PostgREST.
 * Las claves sin "_" (las de las búsquedas guardadas anteriores) pasan igual.
 */
export const toPostgrest = async (filter: Record<string, any>) => {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(filter))
    if (!k.startsWith("_")) out[k] = v;
  const f = filter;
  const has = (k: string) =>
    Array.isArray(f[k])
      ? f[k].length > 0
      : f[k] !== undefined && f[k] !== "" && f[k] !== null && f[k] !== false;

  // Ubicación: zona ∩ estados elegidos
  let estados: string[] | null = has("_estados") ? f._estados : null;
  if (has("_zona")) {
    const z = (f._zona as string[]).flatMap(zonaEstados);
    estados = estados ? estados.filter((e) => z.includes(e)) : z;
  }
  if (estados) out["estado@in"] = inList(estados.length ? estados : ["—"]);
  if (has("_ofertas")) out["fits@ov"] = `{${f._ofertas.join(",")}}`;
  if (has("_sectores")) out["sector@in"] = inList(f._sectores);
  if (has("_tamanos")) out["tamano@in"] = inList(f._tamanos);
  if (has("_tiers")) out["tier@in"] = inList(f._tiers);
  if (has("_fuentes")) out["source@in"] = inList(f._fuentes);
  if (has("_scian"))
    out["scian@like"] = `${String(f._scian).replace(/\D/g, "")}*`;
  if (has("_emp_min")) out["emp_min@gte"] = Number(f._emp_min);
  if (has("_emp_max")) out["emp_min@lte"] = Number(f._emp_max);
  if (has("_prio_min")) out["priority@gte"] = Number(f._prio_min);
  // Termómetro por nivel: [mínimo, siguiente nivel); con una oferta elegida es el de esa oferta
  if (has("_termo")) {
    const min = Number(f._termo);
    const next = [40, 60, 80].find((x) => x > min);
    out["thermo_base@gte"] = min;
    if (next) out["thermo_base@lt"] = next;
  }
  if (has("_tel")) out["telefono@not.is"] = "null";
  if (has("_email")) out["email_tipo@neq"] = "sin_email";
  if (has("_web")) out["sitio_web@not.is"] = "null";
  if (has("_dominio_sin_web")) {
    out["email_tipo"] = "corporativo";
    out["sitio_web@is"] = "null";
  }
  if (has("_alta_desde")) out["alta_denue@gte"] = String(f._alta_desde);
  if (has("_alta_hasta")) out["alta_denue@lte"] = `${f._alta_hasta}-12`;
  // Reseñas: siempre con "reviews_at no nulo" para usar el índice de las analizadas
  if (has("_resenas") || has("_quejas") || has("_rating"))
    out["reviews_at@not.is"] = "null";
  if (has("_quejas")) out["review_pains@ov"] = `{${f._quejas.join(",")}}`;
  const rt = RATINGS.find(([v]) => v === f._rating);
  if (rt) out[`google_rating@${rt[2]}`] = rt[3];
  if (f._ocultar_dnc !== false) out["dnc"] = false; // por defecto: ocultar No contactar

  // C.P. exacto, o C.P. + radio (cuadro alrededor del centro del C.P.: aproximado)
  if (has("_cp") && has("_radio_km")) {
    const { data } = await getSupabaseClient().rpc("cp_centroid", {
      p_cp: String(f._cp),
    });
    const c = data?.[0];
    if (c?.lat != null) {
      const km = Number(f._radio_km);
      const dLat = km / 111;
      const dLng = km / (111 * Math.cos((c.lat * Math.PI) / 180));
      out["lat@gte"] = c.lat - dLat;
      out["lat@lte"] = c.lat + dLat;
      out["lng@gte"] = c.lng - dLng;
      out["lng@lte"] = c.lng + dLng;
    } else out["cp"] = String(f._cp); // C.P. sin coordenadas: exacto
  } else if (has("_cp")) out["cp"] = String(f._cp);
  return out;
};
